import { DEFAULT_THEME, isTheme, THEME_STORAGE_KEY, type Theme } from '@src/theme/theme';

const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

/**
 * Every storage / matchMedia call is guarded: Safari private mode throws on
 * localStorage, and the theme is never important enough to break a render.
 */
function safely<T>(read: () => T, fallback: T): T {
  try {
    return read();
  } catch {
    return fallback;
  }
}

export function readStoredTheme(): Theme | null {
  const stored = safely(() => localStorage.getItem(THEME_STORAGE_KEY), null);
  return isTheme(stored) ? stored : null;
}

export function storeTheme(theme: Theme): void {
  safely(() => localStorage.setItem(THEME_STORAGE_KEY, theme), undefined);
}

export function clearStoredTheme(): void {
  safely(() => localStorage.removeItem(THEME_STORAGE_KEY), undefined);
}

export function prefersDarkTheme(): boolean {
  return safely(() => window.matchMedia(DARK_MEDIA_QUERY).matches, false);
}

/** An explicit choice wins over the OS preference, which wins over the default. */
export function resolveInitialTheme(): Theme {
  return readStoredTheme() ?? (prefersDarkTheme() ? 'dark' : DEFAULT_THEME);
}

/**
 * Writes the attribute the stylesheet reads. Call this before the first render
 * (see main.tsx) — doing it from an effect instead would paint the light theme
 * for one frame first.
 */
export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  const skyColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-landscape-sky-upper').trim();
  const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (themeColor && skyColor) themeColor.content = skyColor;
}
